import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

const TOP_ROW = [12, 13, 14, 15, 16, 17, null, 18, 19, 20, 21, 22, 23];
const BOTTOM_ROW = [11, 10, 9, 8, 7, 6, null, 5, 4, 3, 2, 1, 0];

function sourceKey(value) {
  return String(value);
}

function pointLabel(pointIndex) {
  return pointIndex + 1;
}

function formatMove(move) {
  const from = move.from === 'bar' ? 'Bar' : pointLabel(Number(move.from));
  const to = move.to === 'off' ? 'Off' : pointLabel(Number(move.to));
  return `${from} -> ${to} (${move.die})${move.hits ? ' hit' : ''}`;
}

function buildInstructionState({ gameState, myTurn, localColor, selectedSource, activeMoves }) {
  if (gameState.finished) {
    return {
      title: 'Round complete',
      tone: 'text-emerald-700 dark:text-emerald-300',
      steps: ['Review the result, then use Play Again when the room is ready.'],
    };
  }

  if (!myTurn) {
    return {
      title: 'Waiting for opponent',
      tone: 'text-slate-600 dark:text-slate-300',
      steps: [
        `${gameState.currentTurnColor === 'W' ? 'White' : 'Black'} is taking this turn.`,
        'Watch for hits onto the bar or a chance to re-enter quickly on your next turn.',
      ],
    };
  }

  if (!gameState.hasRolled) {
    return {
      title: `Your turn as ${localColor === 'W' ? 'White' : 'Black'}`,
      tone: 'text-amber-700 dark:text-amber-300',
      steps: [
        'Roll the dice to reveal your legal moves.',
        'If you have checkers on the bar, you must enter them before moving anything else.',
      ],
    };
  }

  if (selectedSource == null) {
    return {
      title: 'Choose a checker stack',
      tone: 'text-sky-700 dark:text-sky-300',
      steps: [
        'Tap a highlighted point or the bar to select the checker you want to move.',
        `You currently have ${activeMoves.length} legal move${activeMoves.length === 1 ? '' : 's'} available.`,
      ],
    };
  }

  return {
    title: 'Complete the move',
    tone: 'text-sky-700 dark:text-sky-300',
    steps: [
      'Tap one of the glowing destinations on the board, or use a move card below.',
      'You must continue using as many dice as the rules allow before the turn ends.',
    ],
  };
}

function statusText(gameState, currentPlayerName) {
  if (gameState.finished) {
    if (gameState.finishReason === 'resignation') return 'Game over by resignation.';
    return 'Game over. A player has borne off all checkers.';
  }
  if (gameState.hasRolled) {
    return `${currentPlayerName || 'Current player'} is moving.`;
  }
  return `${currentPlayerName || 'Current player'} to roll.`;
}

function getLocalColor(gameState, gamePlayers, user) {
  const direct = gameState.playerColors?.[user?.socketId] || gameState.playerColors?.[user?.userId];
  if (direct) return direct;
  const firstPlayer = gamePlayers?.[0];
  if (!firstPlayer) return null;
  const isFirst = firstPlayer.socketId === user?.socketId || firstPlayer.userId === user?.userId;
  return isFirst ? 'W' : 'B';
}

function moveSignature(lastAction) {
  if (!lastAction) return '';
  return JSON.stringify({
    type: lastAction.type,
    by: lastAction.by,
    from: lastAction.from,
    to: lastAction.to,
    die: lastAction.die,
    hits: lastAction.hits,
    dice: lastAction.dice,
    color: lastAction.color,
  });
}

function CheckerStack({ color, count, topAligned, selected, selectable, recentTarget }) {
  if (!color || !count) return null;
  const visibleCount = Math.min(count, 5);
  const shadowClass = color === 'W'
    ? 'bg-white border border-amber-900/25 shadow-[inset_0_-6px_10px_rgba(0,0,0,0.08)]'
    : 'bg-slate-900 border border-white/15 shadow-[inset_0_-6px_12px_rgba(255,255,255,0.08)]';
  const wrapperClass = selected
    ? 'scale-[1.05]'
    : selectable
      ? 'transition-transform duration-200 group-hover:scale-[1.03]'
      : '';

  return (
    <div className={`pointer-events-none absolute inset-x-0 flex justify-center ${topAligned ? 'top-1' : 'bottom-1'}`}>
      <div className="relative flex h-[92px] w-full items-center justify-center sm:h-[112px]">
        {Array.from({ length: visibleCount }).map((_, index) => {
          const offset = index * 16;
          const style = topAligned ? { top: offset } : { bottom: offset };
          return (
            <span
              key={index}
              className={`absolute h-7 w-7 rounded-full transition-all duration-300 sm:h-8 sm:w-8 ${shadowClass} ${wrapperClass} ${recentTarget ? 'animate-backgammon-land shadow-[0_0_0_4px_rgba(125,211,252,0.25)]' : ''}`}
              style={style}
            />
          );
        })}
        {count > 5 ? (
          <span className="absolute right-1 top-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-slate-950 shadow sm:text-xs">
            {count}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Point({ pointIndex, point, onClick, highlighted, selected, selectable, topAligned, recentSource, recentDestination }) {
  const triangleClass = topAligned
    ? 'clip-top-triangle bg-gradient-to-b from-amber-100/90 to-amber-600/90'
    : 'clip-bottom-triangle bg-gradient-to-t from-amber-100/90 to-amber-600/90';

  return (
    <button
      type="button"
      onClick={() => onClick(pointIndex)}
      disabled={!selectable && !highlighted}
      className={`group relative min-h-[128px] w-full rounded-xl transition duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 ${selected ? 'ring-2 ring-sky-400 scale-[1.01]' : ''} ${highlighted ? 'bg-sky-400/10 shadow-[0_0_0_1px_rgba(125,211,252,0.35)]' : ''} ${selectable || highlighted ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default'} ${recentSource ? 'animate-backgammon-source' : ''} ${recentDestination ? 'animate-backgammon-destination' : ''}`}
      aria-label={`Point ${pointLabel(pointIndex)}`}
    >
      <span className={`absolute inset-1 rounded-lg ${triangleClass}`} />
      <span className={`absolute inset-x-1 ${topAligned ? 'bottom-1' : 'top-1'} text-center text-[10px] font-black uppercase tracking-[0.18em] text-slate-950/80 sm:text-xs`}>
        {pointLabel(pointIndex)}
      </span>
      {highlighted ? <span className="absolute inset-2 rounded-lg border-2 border-sky-400 animate-backgammon-target" /> : null}
      {(recentSource || recentDestination) ? <span className="absolute inset-2 rounded-lg bg-white/10" /> : null}
      <CheckerStack color={point?.color} count={point?.count} topAligned={topAligned} selected={selected} selectable={selectable} recentTarget={recentDestination} />
    </button>
  );
}

function BarSlot({ color, count, selected, highlighted, onClick, disabled, label, recentTarget }) {
  const chipClass = color === 'W' ? 'bg-white text-slate-950' : 'bg-slate-950 text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex min-h-[124px] w-full flex-col items-center justify-center rounded-2xl border border-amber-700/40 bg-amber-900/40 px-2 py-3 text-center transition duration-200 ${selected ? 'ring-2 ring-sky-400 scale-[1.01]' : ''} ${highlighted ? 'border-sky-400 bg-sky-400/10' : ''} ${recentTarget ? 'animate-backgammon-destination' : ''} ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-amber-900/55 hover:-translate-y-0.5'}`}
    >
      <span className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-100/80">{label}</span>
      <span className={`mt-3 inline-flex h-10 w-10 items-center justify-center rounded-full text-lg font-black transition-transform duration-300 ${chipClass} ${recentTarget ? 'scale-110' : ''}`}>{count}</span>
    </button>
  );
}

export default function BackgammonGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction, isLocalPlayerTurn, findPlayerById } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [selectedSource, setSelectedSource] = useState(null);
  const [notice, setNotice] = useState('');
  const [recentMoveFx, setRecentMoveFx] = useState(null);
  const [diceAnimating, setDiceAnimating] = useState(false);
  const [recentOffColor, setRecentOffColor] = useState(null);
  const initialMoveSignature = useRef(true);
  const initialRollSignature = useRef(true);
  const previousBorneOffRef = useRef(gameState.borneOff || { W: 0, B: 0 });

  const points = gameState.points || Array.from({ length: 24 }, () => ({ color: null, count: 0 }));
  const legalMoves = gameState.legalMoves || [];
  const myTurn = isLocalPlayerTurn();
  const localColor = useMemo(() => getLocalColor(gameState, gamePlayers, user), [gamePlayers, gameState, user]);
  const currentPlayer = findPlayerById(gameState.currentPlayer);
  const currentPlayerName = currentPlayer?.username || (gameState.currentTurnColor === 'W' ? 'White' : 'Black');

  const movesBySource = useMemo(() => {
    const map = {};
    for (const move of legalMoves) {
      const key = sourceKey(move.from);
      if (!map[key]) map[key] = [];
      map[key].push(move);
    }
    return map;
  }, [legalMoves]);

  const activeSourceKey = selectedSource == null ? null : sourceKey(selectedSource);
  const activeMoves = activeSourceKey ? (movesBySource[activeSourceKey] || []) : legalMoves;
  const highlightedDestinations = useMemo(() => new Set(activeMoves.map((move) => sourceKey(move.to))), [activeMoves]);
  const selectableSources = useMemo(() => new Set(Object.keys(movesBySource)), [movesBySource]);
  const lastActionSignature = useMemo(() => moveSignature(gameState.lastAction), [gameState.lastAction]);
  const lastRollSignature = useMemo(() => JSON.stringify(gameState.lastRoll || []), [gameState.lastRoll]);

  useEffect(() => {
    if (!gameState.lastAction || gameState.lastAction.type !== 'moveChecker') return undefined;
    if (initialMoveSignature.current) {
      initialMoveSignature.current = false;
      return undefined;
    }

    const fx = {
      from: gameState.lastAction.from,
      to: gameState.lastAction.to,
      color: gameState.lastAction.color,
      hits: !!gameState.lastAction.hits,
    };
    setRecentMoveFx(fx);
    const timeout = window.setTimeout(() => setRecentMoveFx(null), 900);
    return () => window.clearTimeout(timeout);
  }, [lastActionSignature, gameState.lastAction]);

  useEffect(() => {
    if (!gameState.lastRoll?.length) return undefined;
    if (initialRollSignature.current) {
      initialRollSignature.current = false;
      return undefined;
    }

    setDiceAnimating(true);
    const timeout = window.setTimeout(() => setDiceAnimating(false), 700);
    return () => window.clearTimeout(timeout);
  }, [lastRollSignature, gameState.lastRoll]);

  useEffect(() => {
    if (selectedSource == null) return;
    if (!selectableSources.has(sourceKey(selectedSource))) {
      setSelectedSource(null);
    }
  }, [selectedSource, selectableSources]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => setNotice(''), 1800);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    const previous = previousBorneOffRef.current || { W: 0, B: 0 };
    const current = gameState.borneOff || { W: 0, B: 0 };
    const changedColor = ['W', 'B'].find((color) => (current[color] || 0) > (previous[color] || 0));
    previousBorneOffRef.current = current;
    if (!changedColor) return undefined;
    setRecentOffColor(changedColor);
    const timeout = window.setTimeout(() => setRecentOffColor(null), 1100);
    return () => window.clearTimeout(timeout);
  }, [gameState.borneOff]);

  const handleResponse = (response) => {
    if (!response || !response.status) return;
    const messages = {
      'not-your-turn': 'Wait for your turn.',
      'already-rolled': 'You have already rolled this turn.',
      'roll-first': 'Roll the dice before moving.',
      'invalid-move': 'That move is not legal.',
      'rolled-no-moves': 'No legal moves. Turn passes automatically.',
      resigned: 'You resigned the game.',
    };
    if (messages[response.status]) setNotice(messages[response.status]);
  };

  const rollDice = () => {
    submitAction({ type: 'rollDice' }).then(handleResponse).catch(() => {});
  };

  const resignGame = () => {
    submitAction({ type: 'resign' }).then(handleResponse).catch(() => {});
  };

  const executeMove = (move) => {
    submitAction({ type: 'moveChecker', from: move.from, to: move.to, die: move.die }).then(handleResponse).catch(() => {});
    setSelectedSource(null);
  };

  const handlePointClick = (pointIndex) => {
    const destinationKey = sourceKey(pointIndex);
    if (selectedSource != null) {
      const chosenMove = activeMoves.find((move) => sourceKey(move.to) === destinationKey);
      if (chosenMove) {
        executeMove(chosenMove);
        return;
      }
    }

    const source = sourceKey(pointIndex);
    if (!selectableSources.has(source)) {
      setSelectedSource(null);
      return;
    }
    setSelectedSource(selectedSource === pointIndex ? null : pointIndex);
  };

  const handleBarClick = () => {
    if (!selectableSources.has('bar')) {
      setSelectedSource(null);
      return;
    }
    setSelectedSource(selectedSource === 'bar' ? null : 'bar');
  };

  const playerCards = useMemo(() => {
    const wins = Object.fromEntries((gameState.players || []).map((player) => [player.socketId, player.score || 0]));
    return ['W', 'B'].map((color) => {
      const playerId = gameState.playersByColor?.[color];
      const player = findPlayerById(playerId);
      return {
        color,
        username: player?.username || (color === 'W' ? 'White' : 'Black'),
        score: playerId ? (wins[playerId] || 0) : 0,
        active: !gameState.finished && gameState.currentTurnColor === color,
        bar: gameState.bar?.[color] || 0,
        off: gameState.borneOff?.[color] || 0,
      };
    });
  }, [findPlayerById, gameState.bar, gameState.borneOff, gameState.currentTurnColor, gameState.finished, gameState.players, gameState.playersByColor]);

  const canRoll = myTurn && !gameState.finished && !gameState.hasRolled;
  const canResign = myTurn && !gameState.finished;
  const canSelectBar = myTurn && selectableSources.has('bar');
  const instructionState = useMemo(
    () => buildInstructionState({ gameState, myTurn, localColor, selectedSource, activeMoves }),
    [activeMoves, gameState, localColor, myTurn, selectedSource]
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 text-slate-900 dark:text-slate-100">
      <div className="rounded-3xl border border-amber-200 bg-white/90 p-4 shadow-sm dark:border-amber-900/70 dark:bg-slate-900/90">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-sm font-black uppercase tracking-[0.26em] text-amber-700 dark:text-amber-300">Backgammon</div>
            <div className="mt-1 text-xl font-semibold">{statusText(gameState, currentPlayerName)}</div>
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {gameState.lastRoll?.length ? `Last roll: ${gameState.lastRoll.join(' and ')}` : 'Roll to begin the turn, then play every legal die you can.'}
            </div>
            {notice ? <div className="mt-2 text-sm font-semibold text-red-600 dark:text-red-300">{notice}</div> : null}
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            <button
              type="button"
              onClick={rollDice}
              disabled={!canRoll}
              className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-500 dark:text-slate-950 dark:hover:bg-amber-400"
            >
              Roll Dice
            </button>
            <button
              type="button"
              onClick={resignGame}
              disabled={!canResign}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Resign
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {(gameState.hasRolled ? gameState.remainingDice : gameState.dice || []).map((die, index) => (
            <div key={`${die}-${index}`} className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-700/20 bg-amber-100 text-xl font-black text-slate-950 shadow-sm dark:border-amber-300/20 dark:bg-amber-300 ${diceAnimating ? 'animate-backgammon-die-roll' : ''}`}>
              {die}
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl border border-amber-200/80 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] dark:border-amber-900/60 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800">
          <div className={`text-sm font-black uppercase tracking-[0.22em] ${instructionState.tone}`}>{instructionState.title}</div>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {instructionState.steps.map((step, index) => (
              <div key={index} className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 text-sm text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-200">
                <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[11px] font-black text-slate-950">{index + 1}</span>
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {playerCards.map((player) => (
          <div key={player.color} className={`rounded-2xl border bg-white/90 p-4 shadow-sm transition-all duration-300 dark:bg-slate-900/90 ${player.active ? 'border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.18),0_18px_35px_rgba(120,53,15,0.12)] dark:border-amber-500' : 'border-slate-200 dark:border-slate-800'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className={`block h-6 w-6 rounded-full ${player.color === 'W' ? 'border border-amber-900/25 bg-white' : 'bg-slate-950'} `} />
                <div>
                  <div className="font-semibold">{player.username}{localColor === player.color ? ' • You' : ''}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Round wins: {player.score}</div>
                </div>
              </div>
              {player.active ? <div className="text-xs font-black uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">Turn</div> : null}
            </div>
            <div className="mt-3 flex gap-4 text-sm text-slate-600 dark:text-slate-300">
              <div>Bar: {player.bar}</div>
              <div className={`${recentOffColor === player.color ? 'animate-backgammon-off-count text-sky-700 dark:text-sky-300' : ''}`}>Off: {player.off}</div>
            </div>
            <div className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              {player.active ? 'Acting now' : 'Waiting'}
            </div>
          </div>
        ))}
      </div>

      <div className="relative overflow-hidden rounded-[2rem] border border-amber-300/70 bg-gradient-to-br from-amber-950 via-amber-900 to-orange-950 p-3 shadow-[0_30px_70px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.08)] dark:border-amber-700/60 sm:p-4">
        <div className="pointer-events-none absolute inset-0 opacity-50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.12),transparent_28%),radial-gradient(circle_at_82%_24%,rgba(255,255,255,0.08),transparent_24%),radial-gradient(circle_at_50%_85%,rgba(15,23,42,0.18),transparent_35%)]" />
          <div className="absolute inset-0 bg-[repeating-linear-gradient(105deg,rgba(255,255,255,0.04)_0px,rgba(255,255,255,0.04)_2px,transparent_2px,transparent_10px),repeating-linear-gradient(12deg,rgba(68,38,12,0.16)_0px,rgba(68,38,12,0.16)_3px,transparent_3px,transparent_12px)] mix-blend-screen" />
          <div className="absolute inset-3 rounded-[1.4rem] border border-white/10" />
        </div>
        <div className="absolute inset-[18px] rounded-[1.55rem] bg-[radial-gradient(circle_at_center,rgba(120,53,15,0.18),transparent_68%)]" />
        <div className="relative grid gap-2" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr)) minmax(72px, 0.85fr) repeat(6, minmax(0, 1fr))' }}>
          {TOP_ROW.map((pointIndex, index) => (
            pointIndex == null ? (
              <div key={`top-bar-${index}`} className="row-span-1">
                <BarSlot
                  color="B"
                  count={gameState.bar?.B || 0}
                  selected={selectedSource === 'bar' && localColor === 'B'}
                  highlighted={selectedSource === 'bar' ? highlightedDestinations.has('bar') : false}
                  recentTarget={recentMoveFx?.hits && recentMoveFx?.color === 'W'}
                  onClick={handleBarClick}
                  disabled={!canSelectBar || localColor !== 'B'}
                  label="Bar"
                />
              </div>
            ) : (
              <Point
                key={`top-${pointIndex}`}
                pointIndex={pointIndex}
                point={points[pointIndex]}
                onClick={handlePointClick}
                highlighted={highlightedDestinations.has(sourceKey(pointIndex))}
                selected={selectedSource === pointIndex}
                selectable={myTurn && selectableSources.has(sourceKey(pointIndex))}
                recentSource={recentMoveFx?.from === pointIndex}
                recentDestination={recentMoveFx?.to === pointIndex}
                topAligned={true}
              />
            )
          ))}

          {BOTTOM_ROW.map((pointIndex, index) => (
            pointIndex == null ? (
              <div key={`bottom-bar-${index}`} className="row-span-1">
                <BarSlot
                  color="W"
                  count={gameState.bar?.W || 0}
                  selected={selectedSource === 'bar' && localColor === 'W'}
                  highlighted={selectedSource === 'bar' ? highlightedDestinations.has('bar') : false}
                  recentTarget={recentMoveFx?.hits && recentMoveFx?.color === 'B'}
                  onClick={handleBarClick}
                  disabled={!canSelectBar || localColor !== 'W'}
                  label="Bar"
                />
              </div>
            ) : (
              <Point
                key={`bottom-${pointIndex}`}
                pointIndex={pointIndex}
                point={points[pointIndex]}
                onClick={handlePointClick}
                highlighted={highlightedDestinations.has(sourceKey(pointIndex))}
                selected={selectedSource === pointIndex}
                selectable={myTurn && selectableSources.has(sourceKey(pointIndex))}
                recentSource={recentMoveFx?.from === pointIndex}
                recentDestination={recentMoveFx?.to === pointIndex}
                topAligned={false}
              />
            )
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Available moves</div>
              <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {selectedSource == null ? 'Select a checker stack or tap a move below.' : 'Filtered to the selected source.'}
              </div>
            </div>
            {selectedSource != null ? (
              <button
                type="button"
                onClick={() => setSelectedSource(null)}
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Clear
              </button>
            ) : null}
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {activeMoves.length ? activeMoves.map((move, index) => (
              <button
                key={`${move.from}-${move.to}-${move.die}-${index}`}
                type="button"
                onClick={() => executeMove(move)}
                className={`rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm font-semibold text-slate-800 transition duration-200 hover:-translate-y-0.5 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-100 dark:hover:bg-amber-900/45 ${selectedSource != null && sourceKey(move.from) === sourceKey(selectedSource) ? 'animate-backgammon-move-card border-sky-400 dark:border-sky-500' : ''} ${recentMoveFx && recentMoveFx.from === move.from && recentMoveFx.to === move.to ? 'animate-backgammon-destination' : ''}`}
              >
                {formatMove(move)}
              </button>
            )) : (
              <div className="rounded-xl border border-dashed border-slate-300 px-3 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {gameState.hasRolled ? 'No move selected or no legal moves remain.' : 'Roll the dice to see legal moves.'}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
          <div className="text-sm font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Off tray</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {playerCards.map((player) => {
              const destinationMoves = activeMoves.filter((move) => move.to === 'off' && localColor === player.color);
              return (
                <button
                  key={player.color}
                  type="button"
                  onClick={() => {
                    if (destinationMoves[0]) executeMove(destinationMoves[0]);
                  }}
                  disabled={!destinationMoves.length}
                  className={`rounded-2xl border p-4 text-left transition duration-200 ${destinationMoves.length ? 'border-sky-400 bg-sky-50 hover:-translate-y-0.5 hover:bg-sky-100 dark:border-sky-500 dark:bg-sky-950/40 dark:hover:bg-sky-950/60' : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/30'} ${recentMoveFx?.to === 'off' && recentMoveFx?.color === player.color ? 'animate-backgammon-destination' : ''} ${recentOffColor === player.color ? 'animate-backgammon-off-count' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`block h-6 w-6 rounded-full ${player.color === 'W' ? 'border border-amber-900/25 bg-white' : 'bg-slate-950'} `} />
                    <div className="font-semibold">{player.username}</div>
                  </div>
                  <div className={`mt-3 text-sm text-slate-600 dark:text-slate-300 ${recentOffColor === player.color ? 'font-bold text-sky-700 dark:text-sky-300' : ''}`}>Borne off: {player.off}</div>
                  {destinationMoves.length ? <div className="mt-2 text-xs font-black uppercase tracking-[0.16em] text-sky-700 dark:text-sky-300">Tap to bear off</div> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .clip-top-triangle {
          clip-path: polygon(50% 100%, 100% 0%, 0% 0%);
        }
        .clip-bottom-triangle {
          clip-path: polygon(0% 100%, 100% 100%, 50% 0%);
        }
        @keyframes backgammonTargetPulse {
          0% { opacity: 0.35; transform: scale(0.94); }
          50% { opacity: 1; transform: scale(1); }
          100% { opacity: 0.35; transform: scale(0.94); }
        }
        @keyframes backgammonLand {
          0% { transform: translateY(-10px) scale(0.92); }
          60% { transform: translateY(2px) scale(1.08); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes backgammonSourceFlash {
          0% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.6); }
          100% { box-shadow: 0 0 0 14px rgba(251, 191, 36, 0); }
        }
        @keyframes backgammonDestinationFlash {
          0% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.7); transform: scale(0.98); }
          60% { box-shadow: 0 0 0 12px rgba(56, 189, 248, 0.08); transform: scale(1.02); }
          100% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); transform: scale(1); }
        }
        @keyframes backgammonDieRoll {
          0% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(-10deg) scale(1.08); }
          50% { transform: rotate(8deg) scale(0.96); }
          100% { transform: rotate(0deg) scale(1); }
        }
        @keyframes backgammonMoveCard {
          0%, 100% { transform: translateY(0) scale(1); box-shadow: 0 0 0 rgba(56, 189, 248, 0); }
          50% { transform: translateY(-3px) scale(1.02); box-shadow: 0 12px 26px rgba(56, 189, 248, 0.18); }
        }
        @keyframes backgammonOffCount {
          0% { transform: scale(1); }
          35% { transform: scale(1.12); }
          65% { transform: scale(0.97); }
          100% { transform: scale(1); }
        }
        .animate-backgammon-target {
          animation: backgammonTargetPulse 1.05s ease-in-out infinite;
        }
        .animate-backgammon-source {
          animation: backgammonSourceFlash 0.7s ease-out 1;
        }
        .animate-backgammon-destination {
          animation: backgammonDestinationFlash 0.85s ease-out 1;
        }
        .animate-backgammon-die-roll {
          animation: backgammonDieRoll 0.65s ease-in-out 1;
        }
        .animate-backgammon-move-card {
          animation: backgammonMoveCard 1.15s ease-in-out infinite;
        }
        .animate-backgammon-off-count {
          animation: backgammonOffCount 0.8s cubic-bezier(0.22, 1, 0.36, 1) 1;
        }
        .animate-backgammon-land {
          animation: backgammonLand 0.42s cubic-bezier(0.22, 1, 0.36, 1) 1, backgammonDestinationFlash 0.85s ease-out 1;
        }
      `}</style>
    </div>
  );
}

BackgammonGame.Guide = function BackgammonGuide() {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold">How to Play Backgammon</h3>
      <p>Roll the dice, then move your checkers according to both values if possible. Doubles are played four times.</p>
      <p>If you have a checker on the bar, you must enter it before moving anything else. Landing on a single opposing checker hits it onto the bar.</p>
      <p>You can only bear off once all fifteen of your checkers are in your home board.</p>
      <p>The first player to bear off all fifteen checkers wins the round.</p>
    </div>
  );
};